/**
 * AtlasEntityView — details pane for one selected wiki entity.
 *
 * Design doc §5:
 *   "Entity View: Displays the accumulated 'what the platform knows'
 *    summary, cross-linked 'Key themes', 'Recent deltas' (last 7 days),
 *    and links back to raw source artefacts."
 *
 * M5 renders: name/type/tenant · summary · every claim + its sources ·
 * the edge fan-out (with clickable targets that swap the selection).
 *
 * KA Wiki Polish (2026-02-07):
 *   - Sources render document filenames instead of raw UUIDs (via the
 *     backend's `source_document_names` enrichment).
 *   - Edges sort by weight desc, cap top-N with a "Show all" toggle,
 *     and show the shared document filename inline.
 *   - Duplicate primary claim (identical to summary) is hidden.
 *   - Type chip is elevated to a proper pill near the concept name.
 */
import React, { useMemo, useState } from 'react';
import { ArrowRight, ExternalLink, FileText, ChevronDown, ChevronUp, Link2, Download } from 'lucide-react';
import { useDocumentDownload } from '../../hooks/useDocumentDownload';
import { HoverTip } from '../HoverTip';

const EDGE_INITIAL_LIMIT = 12;

// Render a synopsis string that contains **bold** section headers into React nodes.
// The M3 synopsis pipeline emits five markers:
//   **Definition.**  **Structural Framework.**  **Corpus Evidence.**
//   **Notable Claims.**  **Adjacent Concepts.**
// These are meant to render as bold labels inline. Rendering the raw string
// leaks the literal asterisks onto the page (bug caught 2026-07-11).
// This helper is intentionally narrow — no full-markdown parser, just **…**.
const renderSynopsisMarkdown = (text) => {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*\n]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={i} className="font-semibold text-cyan-50">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

export const AtlasEntityView = ({
  entity,
  allEntities,
  onSelect,
  sharedSubTopics,
  compareTenant,
  secondarySubTopicCounts,
}) => {
  const byId = useMemo(
    () => Object.fromEntries((allEntities || []).map((e) => [e.entity_id, e])),
    [allEntities]
  );

  const sharedSet = useMemo(
    () => new Set((sharedSubTopics || []).map((s) => (s || '').toLowerCase())),
    [sharedSubTopics]
  );

  const [showAllEdges, setShowAllEdges] = useState(false);
  const { downloadDocument, downloading } = useDocumentDownload();

  // Sort edges by weight desc (fallback: keep original order). We compute this
  // per-entity so switching concepts always shows strongest connections first.
  const sortedEdges = useMemo(() => {
    const list = [...(entity?.edges || [])];
    list.sort((a, b) => (Number(b?.weight ?? 0) - Number(a?.weight ?? 0)));
    return list;
  }, [entity]);

  // Dedupe: if the primary claim's text is identical to the summary, hide it.
  // The claim is redundant — the summary already carries the same content.
  // Kept up here (before the early `return`) so hook-order stays stable
  // across renders regardless of whether an entity is selected.
  const visibleClaims = useMemo(() => {
    const list = entity?.claims || [];
    const sum = entity?.summary;
    if (list.length && sum
        && (list[0]?.text || '').trim() === (sum || '').trim()) {
      return list.slice(1);
    }
    return list;
  }, [entity]);

  if (!entity) {
    return (
      <div
        data-testid="atlas-entity-view-empty"
        className="rounded-lg border border-cyan-400/20 bg-slate-950/60 p-8 text-center text-sm text-cyan-300/70"
      >
        Select a Concept from the Map to see its accumulated summary
        and its Connections.
      </div>
    );
  }

  const {
    entity_id, entity_name, entity_type, tenant, is_seed,
    summary, source_documents, source_document_names,
    first_committed_at,
  } = entity;

  const edgesShown = showAllEdges
    ? sortedEdges
    : sortedEdges.slice(0, EDGE_INITIAL_LIMIT);
  const hiddenEdgeCount = sortedEdges.length - edgesShown.length;

  return (
    <div
      data-testid="atlas-entity-view"
      className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]"
    >
      {/* Left column — summary + claims */}
      <div className="space-y-4">
        <section
          data-testid="atlas-entity-header"
          className="rounded-lg border border-cyan-400/20 bg-slate-950/60 p-5"
        >
          <div className="mb-3 flex items-center gap-2">
            {/* Type chip — a proper pill so operators can eyeball the class
                of concept before reading the description. */}
            <span
              data-testid="atlas-entity-type-chip"
              className="inline-flex items-center rounded-full border border-cyan-400/40 bg-cyan-500/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-200"
            >
              {(entity_type || 'concept').replace(/_/g, ' ')}
            </span>
            {is_seed && (
              <span
                data-testid="atlas-entity-seed-badge"
                className="rounded border border-amber-400/60 bg-amber-500/10 px-1.5 py-[1px] text-[10px] uppercase tracking-wider text-amber-200"
              >
                seed
              </span>
            )}
            {/* H14 Fact-Check badge removed per D9 Cluster 1 ruling
                (2026-02-08). Backend Cartographer H14 coherence gate
                remains intact — only the user-facing chrome is stripped
                so the surface stops broadcasting cryptographic-audit
                internals. */}
          </div>
          <h2
            data-testid="atlas-entity-name"
            className="text-2xl font-semibold text-cyan-50"
          >
            {entity_name || entity_id}
          </h2>
          <div className="mt-1 font-mono text-[11px] text-cyan-300/60">
            {entity_id} · Topic: {tenant}
            {first_committed_at && (
              <> · committed: {new Date(first_committed_at).toISOString().slice(0, 10)}</>
            )}
          </div>
          {/* Multi-Topic overlay affordance — Mockup v0.6. Surfaces
              only when the operator has selected a Secondary Topic
              AND this Concept's Sub-Topic is also present in it. */}
          {compareTenant && sharedSet.has((entity_type || '').toLowerCase()) && (
            <div
              data-testid="atlas-entity-also-in-chip"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-400/60 bg-amber-500/10 px-2.5 py-1 text-[11px] uppercase tracking-widest text-amber-200"
              title={`This Sub-Topic (${entity_type}) is also present in ${compareTenant}`}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: '#fbbf24' }} />
              <span>Also in {compareTenant}</span>
              {secondarySubTopicCounts
                && secondarySubTopicCounts[(entity_type || '').toLowerCase()]
                && (
                  <span className="font-mono text-amber-300/80">
                    · {secondarySubTopicCounts[(entity_type || '').toLowerCase()]} Concepts
                  </span>
                )}
            </div>
          )}
          {summary && (
            <p
              data-testid="atlas-entity-summary"
              className="mt-4 whitespace-pre-wrap text-lg leading-relaxed text-cyan-100/90"
            >
              {renderSynopsisMarkdown(summary)}
            </p>
          )}
        </section>

        {visibleClaims.length > 0 && (
          <section
            data-testid="atlas-entity-claims"
            className="rounded-lg border border-cyan-400/20 bg-slate-950/60 p-5"
          >
            <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-cyan-400/70">
              <FileText className="h-3 w-3" />
              Additional claims &amp; audit trail
            </div>
            <ul className="space-y-3">
              {visibleClaims.map((c, i) => (
                <li key={c.claim_id || i} data-testid="atlas-entity-claim">
                  <div className="text-lg leading-relaxed text-cyan-100/90">
                    {c.text}
                  </div>
                  {(c.sources || []).length > 0 && (
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-cyan-300/60">
                      <span>Sources:</span>
                      {c.sources.map((s) => {
                        const fname = source_document_names?.[s];
                        const label = fname || truncate(s, 20);
                        return (
                          <span
                            key={s}
                            data-testid="atlas-entity-claim-source"
                            className="rounded border border-cyan-400/20 px-1.5 py-[1px] font-mono"
                            title={fname ? s : undefined}
                          >
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {/* Right column — edges + sources */}
      <div className="space-y-4">
        <section
          data-testid="atlas-entity-edges"
          className="rounded-lg border border-cyan-400/20 bg-slate-950/60 p-4"
        >
          <div className="mb-3 flex items-center justify-between text-[11px] uppercase tracking-[0.25em] text-cyan-400/70">
            <span>Connections ({sortedEdges.length})</span>
            {sortedEdges.length > 0 && (
              <span className="relative group inline-flex">
                <span
                  data-testid="atlas-entity-edges-hint"
                  className="normal-case tracking-normal text-[10px] text-cyan-400/50 cursor-help underline decoration-dotted decoration-cyan-400/40 underline-offset-4"
                >
                  sorted by quality
                </span>
                <HoverTip
                  testId="atlas-entity-quality-hover-tag"
                  align="bottom-right"
                  title="Quality"
                  description="Quality reflects how well the evidence in this Topic supports the link between two Concepts. Higher = stronger evidence."
                />
              </span>
            )}
          </div>
          {sortedEdges.length === 0 && (
            <div className="text-[12px] italic text-cyan-300/50">
              No Connections yet.
            </div>
          )}
          <ul className="space-y-1.5">
            {edgesShown.map((e, i) => {
              const target = byId[e.target];
              const targetName = target
                ? (target.entity_name || target.entity_id)
                : truncate(e.target, 24);
              const targetType = target?.entity_type || null;
              const sharedNames = e.shared_document_names || [];
              const weight = Number(e.weight ?? 0);
              // Weight tier for visual emphasis. High >= 0.75, mid 0.4-0.74, low < 0.4.
              const weightTier = weight >= 0.75
                ? 'high'
                : weight >= 0.4 ? 'mid' : 'low';
              const weightColor = {
                high: 'text-emerald-300 border-emerald-400/40',
                mid:  'text-cyan-200 border-cyan-400/30',
                low:  'text-cyan-400/60 border-cyan-400/15',
              }[weightTier];
              return (
                <li key={`${e.target}-${i}`}>
                  <button
                    type="button"
                    data-testid={`atlas-entity-edge-target-${e.target}`}
                    onClick={() => target && onSelect(e.target)}
                    disabled={!target}
                    className="group flex w-full items-center justify-between gap-2 rounded border border-cyan-400/10 bg-slate-900/40 px-2.5 py-1.5 text-left text-[12px] text-cyan-200 transition-colors hover:border-cyan-400/50 hover:bg-slate-900/70 disabled:cursor-not-allowed disabled:opacity-50"
                    title={sharedNames.length
                      ? `Co-occurs in: ${sharedNames.join(', ')}`
                      : (e.type || 'related_to')}
                  >
                    <span className="flex min-w-0 flex-1 items-center gap-2">
                      <Link2 className="h-3 w-3 flex-shrink-0 text-cyan-400/50" />
                      <span className="truncate font-medium text-cyan-100">
                        {targetName}
                      </span>
                      {targetType && (
                        <span className="rounded bg-slate-800/70 px-1.5 py-[1px] text-[9px] uppercase tracking-widest text-cyan-400/60">
                          {targetType.replace(/_/g, ' ')}
                        </span>
                      )}
                    </span>
                    <span className="flex flex-shrink-0 items-center gap-2">
                      <span
                        data-testid={`atlas-entity-edge-weight-${e.target}`}
                        className={`rounded border px-1.5 py-[1px] font-mono text-[9px] ${weightColor}`}
                        title={`Weight (Jaccard): ${weight.toFixed(2)}`}
                      >
                        {weight.toFixed(2)}
                      </span>
                      <ArrowRight className="h-3 w-3 text-cyan-400/40 transition-colors group-hover:text-cyan-300" />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {hiddenEdgeCount > 0 && (
            <button
              type="button"
              data-testid="atlas-entity-edges-toggle"
              onClick={() => setShowAllEdges((v) => !v)}
              className="mt-2 flex w-full items-center justify-center gap-1 rounded border border-dashed border-cyan-400/20 py-1 text-[11px] text-cyan-300/70 transition-colors hover:border-cyan-400/50 hover:text-cyan-200"
            >
              <ChevronDown className="h-3 w-3" />
              Show {hiddenEdgeCount} more
            </button>
          )}
          {showAllEdges && sortedEdges.length > EDGE_INITIAL_LIMIT && (
            <button
              type="button"
              data-testid="atlas-entity-edges-collapse"
              onClick={() => setShowAllEdges(false)}
              className="mt-2 flex w-full items-center justify-center gap-1 rounded border border-dashed border-cyan-400/20 py-1 text-[11px] text-cyan-300/70 transition-colors hover:border-cyan-400/50 hover:text-cyan-200"
            >
              <ChevronUp className="h-3 w-3" />
              Show fewer
            </button>
          )}
        </section>

        {(source_documents && source_documents.length > 0) && (
          <section
            data-testid="atlas-entity-sources"
            className="rounded-lg border border-cyan-400/20 bg-slate-950/60 p-4"
          >
            <div className="mb-3 text-[11px] uppercase tracking-[0.25em] text-cyan-400/70">
              Source documents
            </div>
            <ul className="space-y-1.5">
              {source_documents.map((d) => {
                const fname = source_document_names?.[d];
                // Skip synthetic placeholders (M4 provenance for edges only).
                if (!d || d.startsWith('m4_synthetic')) return null;
                const isDownloading = !!downloading[d];
                return (
                  <li
                    key={d}
                    data-testid="atlas-entity-source-doc"
                    className="text-[11px] text-cyan-200/90"
                  >
                    {fname ? (
                      // Download button — auth'd via JWT (raw <a href> would
                      // 401 because the download endpoint is auth-gated).
                      // Q5 · Public-App Un-hide (2026-02-09) · operator
                      // directive: investors can download the original PDF.
                      <button
                        type="button"
                        onClick={() => downloadDocument(d, fname)}
                        disabled={isDownloading}
                        data-testid="atlas-entity-source-doc-link"
                        className="inline-flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left transition-colors hover:bg-slate-800/60 hover:text-cyan-100 disabled:opacity-50"
                        title={isDownloading ? 'Downloading…' : `Download ${fname}`}
                      >
                        <Download className="h-3 w-3 flex-shrink-0 text-cyan-400/60" />
                        <span className="truncate">{fname}</span>
                        {isDownloading && (
                          <span className="ml-auto text-[9px] uppercase tracking-wider text-cyan-400/70">…</span>
                        )}
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 font-mono opacity-70">
                        <ExternalLink className="h-3 w-3 flex-shrink-0 text-cyan-400/60" />
                        {truncate(d, 40)}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
};

const truncate = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + '…' : s);

export default AtlasEntityView;
